/**
 * NoPublishedReferencesPage — neutral replacement for the former
 * testimonials / clients / case-studies pages.
 *
 * INTEGRITY: those pages carried invented client names, invented quotes and
 * invented result figures. They were removed outright. This page states only
 * what the service does, with no figures, no quotes and no attributed people.
 * It is noindex so it does not compete for proof-intent queries.
 */
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { MainLayout } from '@/components/layout/MainLayout';
import { SEOHead } from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContextNew';

type Copy = { title: string; desc: string; h1: string; body: string; body2: string; cta: string };

const COPY: Record<string, Copy> = {
  en: {
    title: 'Client References | BPO Contact',
    desc: 'How our multilingual contact center and development teams work, and how to request client references directly.',
    h1: 'Client references',
    body: 'We do not publish client names, quotes or performance figures on this site. Contracts with our clients cover confidentiality, and we will not present unverified numbers as results.',
    body2: 'We run multilingual customer support, technical support and back-office operations 24/7, plus software development and QA teams. References relevant to your sector and languages can be shared under NDA once we understand your scope.',
    cta: 'Request references',
  },
  fr: {
    title: 'Références clients | BPO Contact',
    desc: 'Comment travaillent nos équipes de centre de contact multilingue et de développement, et comment demander des références clients.',
    h1: 'Références clients',
    body: 'Nous ne publions ni noms de clients, ni citations, ni chiffres de performance sur ce site. Nos contrats prévoient la confidentialité, et nous ne présentons pas de chiffres non vérifiés comme des résultats.',
    body2: 'Nous opérons le support client, le support technique et le back-office multilingues 24/7, ainsi que des équipes de développement logiciel et de QA. Des références pertinentes pour votre secteur et vos langues peuvent être partagées sous NDA.',
    cta: 'Demander des références',
  },
  nl: {
    title: 'Klantreferenties | BPO Contact',
    desc: 'Hoe onze meertalige contactcenter- en ontwikkelteams werken, en hoe u klantreferenties opvraagt.',
    h1: 'Klantreferenties',
    body: 'Wij publiceren geen klantnamen, citaten of prestatiecijfers op deze site. Onze contracten bevatten vertrouwelijkheidsafspraken, en wij presenteren geen niet-geverifieerde cijfers als resultaten.',
    body2: 'Wij verzorgen meertalige klantenservice, technische support en back-office 24/7, plus softwareontwikkeling en QA. Referenties die passen bij uw sector en talen delen we onder NDA.',
    cta: 'Referenties aanvragen',
  },
  es: {
    title: 'Referencias de clientes | BPO Contact',
    desc: 'Cómo trabajan nuestros equipos de contact center multilingüe y de desarrollo, y cómo solicitar referencias.',
    h1: 'Referencias de clientes',
    body: 'No publicamos nombres de clientes, citas ni cifras de rendimiento en este sitio. Nuestros contratos incluyen confidencialidad y no presentamos cifras no verificadas como resultados.',
    body2: 'Operamos atención al cliente, soporte técnico y back-office multilingües 24/7, además de equipos de desarrollo y QA. Podemos compartir referencias bajo NDA.',
    cta: 'Solicitar referencias',
  },
  de: {
    title: 'Kundenreferenzen | BPO Contact',
    desc: 'Wie unsere mehrsprachigen Contact-Center- und Entwicklungsteams arbeiten und wie Sie Referenzen anfragen.',
    h1: 'Kundenreferenzen',
    body: 'Wir veröffentlichen auf dieser Website keine Kundennamen, Zitate oder Leistungszahlen. Unsere Verträge sehen Vertraulichkeit vor, und wir stellen keine unbestätigten Zahlen als Ergebnisse dar.',
    body2: 'Wir betreiben mehrsprachigen Kundenservice, technischen Support und Backoffice rund um die Uhr sowie Software- und QA-Teams. Referenzen teilen wir unter NDA.',
    cta: 'Referenzen anfragen',
  },
  it: {
    title: 'Referenze clienti | BPO Contact',
    desc: 'Come lavorano i nostri team di contact center multilingue e di sviluppo, e come richiedere referenze.',
    h1: 'Referenze clienti',
    body: 'Non pubblichiamo nomi di clienti, citazioni o dati di performance su questo sito. I nostri contratti prevedono la riservatezza e non presentiamo cifre non verificate come risultati.',
    body2: 'Gestiamo assistenza clienti, supporto tecnico e back-office multilingue 24/7, oltre a team di sviluppo software e QA. Le referenze sono condivise sotto NDA.',
    cta: 'Richiedi referenze',
  },
  pt: {
    title: 'Referências de clientes | BPO Contact',
    desc: 'Como trabalham as nossas equipas de contact center multilíngue e de desenvolvimento, e como pedir referências.',
    h1: 'Referências de clientes',
    body: 'Não publicamos nomes de clientes, citações ou números de desempenho neste site. Os nossos contratos incluem confidencialidade e não apresentamos números não verificados como resultados.',
    body2: 'Operamos atendimento ao cliente, suporte técnico e back-office multilíngue 24/7, além de equipas de desenvolvimento e QA. Referências são partilhadas sob NDA.',
    cta: 'Pedir referências',
  },
  ar: {
    title: 'مراجع العملاء | BPO Contact',
    desc: 'كيف تعمل فرق مركز الاتصال متعدد اللغات وفرق التطوير، وكيفية طلب مراجع العملاء.',
    h1: 'مراجع العملاء',
    body: 'لا ننشر أسماء العملاء أو الاقتباسات أو أرقام الأداء على هذا الموقع. عقودنا تشمل السرية، ولا نقدم أرقاماً غير موثقة كنتائج.',
    body2: 'ندير خدمة العملاء والدعم الفني والمكتب الخلفي متعدد اللغات على مدار الساعة، بالإضافة إلى فرق تطوير البرمجيات وضمان الجودة. تُشارك المراجع بموجب اتفاقية سرية.',
    cta: 'طلب المراجع',
  },
};

export default function NoPublishedReferencesPage() {
  const { language, isRTL } = useLanguage();
  const t = COPY[language] || COPY.en;

  return (
    <MainLayout>
      <SEOHead title={t.title} description={t.desc} noIndex />
      <section dir={isRTL ? 'rtl' : 'ltr'} className="py-20 bg-background">
        <div className="container mx-auto px-4 max-w-3xl">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-6">{t.h1}</h1>
          <p className="text-lg text-muted-foreground mb-4">{t.body}</p>
          <p className="text-lg text-muted-foreground mb-8">{t.body2}</p>
          <Button asChild size="lg">
            <Link to={`/${language}/contact`} className="inline-flex items-center gap-2">
              {t.cta}
              <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
            </Link>
          </Button>
        </div>
      </section>
    </MainLayout>
  );
}
